import { Suspense, lazy, useMemo, useState } from "react"
import { motion } from "motion/react"
import { ArrowLeft, ArrowRight, Check } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import {
  PANELS_BY_ID,
  SEVERITY_LABEL,
  DAMAGE_TYPE_LABEL,
  type DamageTag,
  type DamageType,
  type DamageSeverity,
  type PanelSpec,
} from "./panels"

// The 3D module (three/r3f/drei) is code-split — non-tagging routes pay zero.
const Car3D = lazy(() => import("./car-3d"))

function webglAvailable(): boolean {
  try {
    const c = document.createElement("canvas")
    return !!(
      window.WebGLRenderingContext &&
      (c.getContext("webgl") || c.getContext("experimental-webgl"))
    )
  } catch {
    return false
  }
}

interface StepDamageTagProps {
  tags: DamageTag[]
  /** Toggle a panel on/off (owner decides add vs remove). */
  onTogglePanel: (panel: PanelSpec) => void
  onRemove: (panelId: string) => void
  onUpdateTag: (panelId: string, patch: Partial<DamageTag>) => void
  onContinue: () => void
  onBack?: () => void
  /** Rendered when WebGL is unavailable (the 2D fallback selector). */
  fallback?: React.ReactNode
}

export function StepDamageTag({
  tags,
  onTogglePanel,
  onRemove,
  onUpdateTag,
  onContinue,
  onBack,
  fallback,
}: StepDamageTagProps) {
  const hasWebgl = useMemo(webglAvailable, [])
  const selectedIds = useMemo(() => new Set(tags.map((t) => t.panelId)), [tags])
  const [openTagId, setOpenTagId] = useState<string | null>(null)

  return (
    <div className="space-y-6">
      <header>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          Step 2 of 4 · Tag the damage
        </p>
        <h2 className="text-2xl font-bold leading-tight tracking-tight text-zinc-900 md:text-[28px]">
          Where’s the damage?
        </h2>
        <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
          Spin the car and tap every damaged panel. We’ll walk you through the
          photos for each one next — so the estimate knows exactly what it’s
          looking at.
        </p>
      </header>

      {/* 3D stage (or 2D fallback when WebGL is unavailable). */}
      <div className="relative overflow-hidden rounded-3xl border border-zinc-200/70 bg-zinc-100">
        {hasWebgl ? (
          <div className="h-[360px] md:h-[420px]">
            <Suspense
              fallback={
                <div className="grid h-full place-items-center">
                  <span className="size-6 animate-spin rounded-full border-2 border-zinc-300 border-t-emerald-500" />
                </div>
              }
            >
              <Car3D selectedIds={selectedIds} onToggle={onTogglePanel} />
            </Suspense>
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-zinc-900/70 px-3 py-1.5 text-[11.5px] font-medium text-white backdrop-blur">
              Drag to rotate · tap a panel
            </div>
          </div>
        ) : (
          (fallback ?? (
            <div className="grid h-[360px] place-items-center px-6 text-center">
              <p className="text-[13px] text-zinc-500">
                3D view isn’t available on this device — tap the panels below
                instead.
              </p>
            </div>
          ))
        )}
      </div>

      {/* Tagged panels — tap a chip to set severity/type or remove. */}
      {tags.length > 0 && (
        <div className="space-y-2">
          <p className="text-[12.5px] font-semibold text-zinc-500">
            {tags.length} {tags.length === 1 ? "panel" : "panels"} tagged
          </p>
          <div className="space-y-2">
            {tags.map((t) => (
              <TagRow
                key={t.panelId}
                tag={t}
                open={openTagId === t.panelId}
                onOpen={() =>
                  setOpenTagId((id) => (id === t.panelId ? null : t.panelId))
                }
                onRemove={() => onRemove(t.panelId)}
                onUpdate={(patch) => onUpdateTag(t.panelId, patch)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2 pt-1">
        <button
          type="button"
          disabled={tags.length === 0}
          onClick={onContinue}
          className={cx(
            "flex h-16 w-full items-center justify-center gap-2.5 rounded-2xl text-[17px] font-semibold transition-all duration-150",
            tags.length === 0
              ? "cursor-not-allowed bg-zinc-200 text-zinc-400"
              : "bg-zinc-900 text-white shadow-xl shadow-zinc-900/20 hover:bg-black hover:shadow-zinc-900/30",
          )}
        >
          {tags.length === 0
            ? "Tap a damaged panel to continue"
            : `Photograph ${tags.length} ${tags.length === 1 ? "area" : "areas"}`}
          {tags.length > 0 && <ArrowRight className="size-5" aria-hidden />}
        </button>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-10 w-full items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 transition-colors duration-150 hover:text-zinc-900"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Back
          </button>
        )}
      </div>
    </div>
  )
}

const SEVERITIES: DamageSeverity[] = ["minor", "moderate", "severe"]
const DAMAGE_TYPES: DamageType[] = [
  "dent",
  "scratch",
  "crack",
  "broken",
  "missing",
  "misaligned",
]

function TagRow({
  tag,
  open,
  onOpen,
  onRemove,
  onUpdate,
}: {
  tag: DamageTag
  open: boolean
  onOpen: () => void
  onRemove: () => void
  onUpdate: (patch: Partial<DamageTag>) => void
}) {
  const panel = PANELS_BY_ID[tag.panelId]
  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200/70 bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-emerald-500">
          <Check className="size-4 text-white" strokeWidth={3} aria-hidden />
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
        >
          <p className="truncate text-[14px] font-semibold leading-tight text-zinc-900">
            {panel?.label ?? tag.label}
          </p>
          <p className="text-[12px] leading-tight text-zinc-500">
            {SEVERITY_LABEL[tag.severity]} · {DAMAGE_TYPE_LABEL[tag.damageType]}
          </p>
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="grid size-7 shrink-0 place-items-center rounded-full text-[15px] font-bold text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
          aria-label={`Remove ${panel?.label ?? tag.label}`}
        >
          ×
        </button>
      </div>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="overflow-hidden border-t border-zinc-100 px-4 py-3"
        >
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-400">
            Severity
          </p>
          <div className="mb-3 flex gap-1.5">
            {SEVERITIES.map((s) => (
              <Pill
                key={s}
                active={tag.severity === s}
                onClick={() => onUpdate({ severity: s })}
              >
                {SEVERITY_LABEL[s]}
              </Pill>
            ))}
          </div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-400">
            Type
          </p>
          <div className="flex flex-wrap gap-1.5">
            {DAMAGE_TYPES.map((d) => (
              <Pill
                key={d}
                active={tag.damageType === d}
                onClick={() => onUpdate({ damageType: d })}
              >
                {DAMAGE_TYPE_LABEL[d]}
              </Pill>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-all duration-150",
        active
          ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
          : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200/80",
      )}
    >
      {children}
    </button>
  )
}
